import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sessoesApi } from '../api/sessoes'
import { usuariosApi } from '../api/usuarios'
import SessoesTable from '../components/Sessoes/SessoesTable'
import ExportButton from '../components/ExportButton'
import ActionBar from '../components/ActionBar'

const SessoesPage: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  
  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'created_at', label: 'Criado em', format: (v: any) => v ? new Date(v).toLocaleString('pt-BR') : '' },
    { key: 'last_activity', label: 'Última Atividade', format: (v: any) => v ? new Date(v).toLocaleString('pt-BR') : '' },
    { key: 'ip_address', label: 'IP' },
    { key: 'user_agent', label: 'User Agent' },
    { key: 'is_active', label: 'Ativo', format: (v: any) => v ? 'Sim' : 'Não' },
  ], [])
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Buscar usuários para o select
  const { data: usuarios = [] } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => usuariosApi.list(),
  })
  const safeUsuarios = Array.isArray(usuarios) ? usuarios : []

  // Buscar sessões do usuário selecionado
  const {
    data: sessoes = [],
    isLoading: loadingSessoes,
    error: errorSessoes,
  } = useQuery({
    queryKey: ['sessoes', selectedUsuarioId],
    queryFn: () => sessoesApi.listByUsuario(selectedUsuarioId!),
    enabled: selectedUsuarioId !== null,
  })

  // Mutação para revogar sessão
  const revogarMutation = useMutation({
    mutationFn: (sessaoId: number) => sessoesApi.revogar(sessaoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessoes', selectedUsuarioId] })
      setSnackbar({
        open: true,
        message: t('sessions.revokeSuccess'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('sessions.title'),
        severity: 'error',
      })
    },
  })

  // Mutação para revogar todas as sessões
  const revogarTodasMutation = useMutation({
    mutationFn: (usuarioId: number) => sessoesApi.revogarTodas(usuarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessoes', selectedUsuarioId] })
      setSnackbar({
        open: true,
        message: t('sessions.revokeAllSuccess'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('sessions.title'),
        severity: 'error',
      })
    },
  })

  const handleRevogar = async (sessaoId: number) => {
    await revogarMutation.mutateAsync(sessaoId)
  }

  const handleRevogarTodas = async () => {
    if (selectedUsuarioId) {
      await revogarTodasMutation.mutateAsync(selectedUsuarioId)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ActionBar>
          {selectedUsuarioId && (
            <ExportButton
              data={Array.isArray(sessoes) ? sessoes : []}
              columns={exportColumns}
              filename="sessoes.xlsx"
              sheetName="Sessões"
            />
          )}
        </ActionBar>

        <Box mb={3}>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>{t('sessions.selectUser')}</InputLabel>
            <Select
              value={selectedUsuarioId || ''}
              label={t('sessions.selectUser')}
              onChange={(e) => setSelectedUsuarioId(Number(e.target.value))}
            >
              {safeUsuarios.map((usuario) => (
                <MenuItem key={usuario.id} value={usuario.id}>
                  {usuario.name} ({usuario.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {selectedUsuarioId ? (
            <>
              {loadingSessoes ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              ) : errorSessoes ? (
                <Alert severity="error">
                  Erro ao carregar sessões: {(errorSessoes as Error).message}
                </Alert>
              ) : (
                <SessoesTable
                  sessoes={Array.isArray(sessoes) ? sessoes : []}
                  onRevogar={handleRevogar}
                  onRevogarTodas={handleRevogarTodas}
                  loading={revogarMutation.isPending || revogarTodasMutation.isPending}
                />
              )}
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">
                Selecione um usuário para ver suas sessões
              </Typography>
            </Box>
          )}
        </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SessoesPage
